// LeadDetail container — three-column layout per mockup. Orchestrates
// LeftPanel + Center + RightPanel against a single shared `lead` record.
import React, { Component } from "react";
import ReactDOM from "react-dom";
import { Link } from "react-router-dom";
import HelperClass from "../../../../config/helperClass";
import { getLead, listLeadsGrid } from "../api/leads_api";
import { addNewClient, sendClientInvite } from "../../../../config/api_calls";
import LeftPanel from "./LeftPanel";
import Center from "./Center";
import RightPanel from "./RightPanel";
import { daysSince } from "./helpers";
import NotificationToast from "../../../../components/Reseller/NotificationToast";
import "./LeadDetail.css";

const auth = new HelperClass();

function priorityBadge(p) {
  const priority = String(p || "").toLowerCase();
  if (priority === "high") return <span className="ld-hdr-priority ld-hdr-priority-high">🔥 High Priority</span>;
  if (priority === "med" || priority === "medium") return <span className="ld-hdr-priority ld-hdr-priority-medium">⚠️ Medium Priority</span>;
  if (priority === "low") return <span className="ld-hdr-priority ld-hdr-priority-low">Low Priority</span>;
  return null;
}

function toNumber(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function deriveDealValueFromProducts(products, fallback) {
  const list = Array.isArray(products) ? products : [];
  const hasAnyRate = list.some((p) => p && p.rate != null && p.rate !== "");
  if (!hasAnyRate) return fallback;
  const total = list.reduce((sum, p) => sum + toNumber(p && p.rate), 0);
  return total;
}

function mergeRates(baseProducts, sourceProducts) {
  const base = Array.isArray(baseProducts) ? baseProducts : [];
  const src = Array.isArray(sourceProducts) ? sourceProducts : [];
  const rateMap = {};
  src.forEach((p) => {
    if (p && p.service_id != null && p.rate != null && p.rate !== "") {
      rateMap[String(p.service_id)] = p.rate;
    }
  });
  return base.map((p) => {
    const sid = p && p.service_id != null ? String(p.service_id) : null;
    if (!sid || !Object.prototype.hasOwnProperty.call(rateMap, sid)) return p;
    return Object.assign({}, p, { rate: rateMap[sid] });
  });
}

export default class LeadDetail extends Component {
  state = {
    loading: true,
    error: "",
    lead: null,
    products: [],
    activities: [],
    notes: [],
    partner: null,
    conversion: null,
    tracking: null,
    toast: null,
    showConvertModal: false,
    converting: false,
    convertErr: "",
  };

  async componentDidMount() {
    await this.load();
    this._onKeyDown = (e) => {
      if (e.key === "Escape" && this.state.showConvertModal && !this.state.converting) this.closeConvertModal();
    };
    document.addEventListener("keydown", this._onKeyDown);
  }

  componentWillUnmount() {
    if (this._onKeyDown) document.removeEventListener("keydown", this._onKeyDown);
    clearTimeout(this._toastTimer);
  }

  async componentDidUpdate(prevProps) {
    const prev = prevProps && prevProps.props && prevProps.props.match && prevProps.props.match.params && prevProps.props.match.params.lead_uid;
    const now = this.props && this.props.props && this.props.props.match && this.props.props.match.params && this.props.props.match.params.lead_uid;
    if (prev !== now) await this.load();
  }

  getUid() {
    return this.props && this.props.props && this.props.props.match
      && this.props.props.match.params && this.props.props.match.params.lead_uid;
  }

  load = async () => {
    this.setState({ loading: true, error: "" });
    const lead_uid = this.getUid();
    if (!lead_uid) { this.setState({ loading: false, error: "Missing lead id." }); return; }
    const findRow = (resp) => resp && resp.data && resp.data.data && (resp.data.data.rows || []).find((r) => r.lead_uid === lead_uid);
    const activeResp = await listLeadsGrid(auth.getAccount(), auth.getToken(), { search: lead_uid, page: 1, page_size: 5 });
    let row = findRow(activeResp);
    if (!row) {
      const archivedResp = await listLeadsGrid(auth.getAccount(), auth.getToken(), { search: lead_uid, page: 1, page_size: 5, show_archived: true });
      row = findRow(archivedResp);
    }
    if (!row) { this.setState({ loading: false, error: "Lead not found." }); return; }
    await this.refresh(row.id, row.products || []);
  };

  refresh = async (id, gridProducts) => {
    const lead_id = id || (this.state.lead ? this.state.lead.id : null);
    if (!lead_id) return;
    const r = await getLead(auth.getAccount(), auth.getToken(), lead_id);
    if (r && r.data && r.data.status === 200) {
      const d = r.data.data;
      const products = mergeRates(d.products || [], gridProducts || this.state.products || []);
      const lead = Object.assign({}, d.lead, {
        deal_value: deriveDealValueFromProducts(products, d && d.lead ? d.lead.deal_value : 0),
      });
      this.setState({
        loading: false,
        lead,
        products,
        activities: d.activities || [],
        notes: d.notes || [],
        partner: d.partner || null,
        conversion: d.conversion || null,
        tracking: d.tracking || null,
      });
    } else {
      this.setState({ loading: false, error: "Failed to load lead." });
    }
  };

  showToast = (msg, isError) => {
    clearTimeout(this._toastTimer);
    this.setState({ toast: { msg: String(msg), type: isError ? "error" : "success" } });
    this._toastTimer = setTimeout(() => this.setState({ toast: null }), 2500);
  };

  onFieldSaved = (key, value) => {
    // Optimistic local update; full refresh fetches new audit activity row + score.
    this.setState({ lead: Object.assign({}, this.state.lead, { [key]: value }) });
    this.refresh();
  };

  onStageChanged = (stage_key, conflict, promptConvert) => {
    if (conflict) { this.refresh(); return; }
    if (stage_key) {
      this.setState({ lead: Object.assign({}, this.state.lead, { stage_key }) });
    }
    this.refresh();
    if (promptConvert) this.showToast("Stage marked as Won — open Add as Client to convert.", false);
  };

  onConvert = () => {
    this.setState({ showConvertModal: true, convertErr: "" });
  };

  closeConvertModal = () => {
    if (this.state.converting) return;
    this.setState({ showConvertModal: false, convertErr: "" });
  };

  doConvert = async (action) => {
    const { lead } = this.state;
    if (!lead) return;
    this.setState({ converting: true, convertErr: "" });
    try {
      var r;
      if (action === "invite") {
        r = await sendClientInvite(
          auth.getAccount(),
          auth.getToken(),
          lead.partner_accountno || "",
          lead.email || "",
          lead.contact_name || lead.company || "",
          ""
        );
      } else {
        r = await addNewClient(
          auth.getAccount(),
          auth.getToken(),
          "",
          lead.contact_name || lead.company || "",
          lead.email || "",
          "",
          lead.phone || "",
          lead.company || "",
          lead.address || "",
          "",
          "",
          "",
          "",
          lead.country || "",
          lead.partner_accountno || "",
          action === "add_notify" ? "yes" : "no",
          "active",
          "no",
          "",
          ""
        );
      }
      var ok = r && r.data && r.data.status === 200;
      if (ok) {
        this.setState({ converting: false, showConvertModal: false });
        var msgs = {
          add_notify: (lead.company || "Lead") + " added as client — welcome email sent.",
          add_silent: (lead.company || "Lead") + " added as client. No email sent.",
          invite: "Self-registration invite sent to " + (lead.email || "the contact") + "."
        };
        this.showToast(msgs[action] || "Done.", false);
      } else {
        var rawMsg = (r && r.data && (r.data.message || (r.data.errors && r.data.errors.message))) || "Action failed. Please try again.";
        var errMsg = String(rawMsg);
        if (errMsg.toLowerCase().indexOf("email_exists") !== -1 || errMsg.toLowerCase().indexOf("email exists") !== -1) {
          errMsg = "A client account with this email address already exists. Please use a different email or locate the existing client record.";
        }
        this.setState({ converting: false, convertErr: errMsg });
      }
    } catch (e) {
      this.setState({ converting: false, convertErr: String(e && e.message ? e.message : e) });
    }
  };

  onArchived = () => {
    // Redirect back to leads list after archive.
    if (window && window.location) window.location.href = "/crm_leads";
  };

  renderConvertModal(lead) {
    const { showConvertModal, converting, convertErr } = this.state;
    if (!showConvertModal) return null;
    return (
      <div
        style={{
          position: "fixed", inset: 0, background: "rgba(15,14,13,0.45)",
          zIndex: 1050, display: "flex", alignItems: "center",
          justifyContent: "center", padding: 24,
        }}
        onClick={this.closeConvertModal}
      >
        <div
          style={{
            background: "#fff", borderRadius: 16,
            boxShadow: "0 24px 48px rgba(0,0,0,0.14),0 8px 16px rgba(0,0,0,0.08)",
            width: "100%", maxWidth: 520, overflow: "hidden",
          }}
          onClick={function(e) { e.stopPropagation(); }}
        >
          {/* Header */}
          <div style={{ padding: "22px 26px 18px", borderBottom: "1px solid #E2DFD9", display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 14 }}>
            <div>
              <div style={{ fontSize: 16, fontWeight: 700, color: "#1C1B19", letterSpacing: "-0.01em", marginBottom: 2 }}>Add as Client</div>
              <div style={{ fontSize: 12.5, color: "#6B6860" }}>Choose how you want to onboard <strong>{lead.company}</strong></div>
            </div>
            <div
              onClick={this.closeConvertModal}
              style={{ width: 28, height: 28, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", color: "#9A9690", cursor: "pointer", flexShrink: 0 }}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="15" height="15"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </div>
          </div>
          {/* Body */}
          <div style={{ padding: "22px 26px" }}>
            {convertErr ? (
              <div className="example-alert" style={{ marginBottom: 14 }}>
                <div className="alert alert-pro alert-danger">
                  <div className="alert-text">
                    <h4>Error</h4>
                    <p style={{ fontSize: 13 }}>{convertErr}</p>
                  </div>
                </div>
              </div>
            ) : null}
            {/* Option 1: Add & Notify */}
            <div
              className="ld-mac-opt"
              onClick={converting ? null : function() { this.doConvert("add_notify"); }.bind(this)}
              style={{ cursor: converting ? "not-allowed" : "pointer", opacity: converting ? 0.6 : 1 }}
            >
              <div className="ld-mac-opt-ico" style={{ background: "rgba(26,122,74,0.12)", color: "#1A7A4A" }}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" width="22" height="22"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/><polyline points="9 11 11 13 15 9"/></svg>
              </div>
              <div className="ld-mac-opt-body">
                <div className="ld-mac-opt-ttl">Add &amp; Notify by Email</div>
                <div className="ld-mac-opt-sub">Create the client record and send a welcome email with their portal credentials.</div>
              </div>
              <svg className="ld-mac-opt-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="18" height="18"><polyline points="9 18 15 12 9 6"/></svg>
            </div>
            {/* Option 2: Add Silent */}
            <div
              className="ld-mac-opt"
              onClick={converting ? null : function() { this.doConvert("add_silent"); }.bind(this)}
              style={{ cursor: converting ? "not-allowed" : "pointer", opacity: converting ? 0.6 : 1 }}
            >
              <div className="ld-mac-opt-ico" style={{ background: "rgba(28,91,222,0.12)", color: "#1C5BDE" }}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" width="22" height="22"><path d="M16 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="8.5" cy="7" r="4"/><polyline points="17 11 19 13 23 9"/></svg>
              </div>
              <div className="ld-mac-opt-body">
                <div className="ld-mac-opt-ttl">Add Without Notification</div>
                <div className="ld-mac-opt-sub">Create the client record silently. Send credentials manually later.</div>
              </div>
              <svg className="ld-mac-opt-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="18" height="18"><polyline points="9 18 15 12 9 6"/></svg>
            </div>
            {/* Option 3: Self-registration invite */}
            <div
              className="ld-mac-opt"
              onClick={converting ? null : function() { this.doConvert("invite"); }.bind(this)}
              style={{ cursor: converting ? "not-allowed" : "pointer", opacity: converting ? 0.6 : 1, marginBottom: 0 }}
            >
              <div className="ld-mac-opt-ico" style={{ background: "rgba(217,119,6,0.12)", color: "#D97706" }}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" width="22" height="22"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
              </div>
              <div className="ld-mac-opt-body">
                <div className="ld-mac-opt-ttl">Send Self-Registration Invite</div>
                <div className="ld-mac-opt-sub">Email a registration link so the customer creates their own account.</div>
              </div>
              <svg className="ld-mac-opt-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="18" height="18"><polyline points="9 18 15 12 9 6"/></svg>
            </div>
            {converting ? <div style={{ textAlign: "center", marginTop: 14, fontSize: 13, color: "#6B6860" }}>Processing…</div> : null}
          </div>
        </div>
      </div>
    );
  }

  renderHeader(lead) {
    const days = daysSince(lead.created_at);
    return (
      <div className="nk-block-head nk-block-head-sm">
        <div className="nk-block-between" style={{ gap: 12, flexWrap: "wrap" }}>
          <div className="nk-block-head-content" style={{ minWidth: 0, flex: "1 1 0", overflow: "hidden" }}>
            <div className="ld-hdr-crumb">
              <Link to="/crm_leads" className="ld-hdr-crumb-link">All Leads</Link> › {lead.lead_uid}
            </div>
            <div
              className="ld-hdr-title"
              title={lead.company || ""}
              style={{ maxWidth: "100%", minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
            >{lead.company}</div>
            <div className="ld-hdr-sub" style={{ minWidth: 0, maxWidth: "100%" }}>
              <span
                className="ld-stage-pill"
                title={lead.stage_label || lead.stage_key || ""}
                style={{
                  background: "#e8eefb", color: "#1c5bde",
                  maxWidth: "100%", minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                }}
              >{lead.stage_label || lead.stage_key}</span>
              <span className="ld-hdr-dot">·</span>
              <span
                title={lead.contact_name || ""}
                style={{ maxWidth: 240, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "inline-block", verticalAlign: "middle" }}
              >{lead.contact_name || "—"}</span>
              <span className="ld-hdr-dot">·</span>
              <span>Created {lead.created_at ? new Date(lead.created_at).toLocaleDateString() : "—"}{days != null ? " · " + days + " day" + (days === 1 ? "" : "s") + " active" : ""}</span>
              {priorityBadge(lead.priority)}
            </div>
          </div>
          <div className="nk-block-head-content" id="HeadContentLeadDetail">
            <div className="toggle-wrap nk-block-tools-toggle">
              <a href="#" className="btn btn-icon btn-trigger toggle-expand mr-n1" data-target="leadDetailMenu">
                <em className="icon ni ni-more-v"></em>
              </a>
              <div className="toggle-expand-content" data-content="leadDetailMenu">
                <ul className="nk-block-tools g-3">
                  <li className="nk-block-tools-opt">
                    <Link to="/crm_leads" className="btn btn-info" style={{ color: "#fff" }}>
                      <em className="icon ni ni-arrow-left"></em>&nbsp;<span>All Leads</span>
                    </Link>
                  </li>
                  <li className="nk-block-tools-opt">
                    <button type="button" className="btn btn-primary" style={{ color: "#fff" }} onClick={this.onConvert}>
                      <em className="icon ni ni-plus"></em>&nbsp;<span>Add as Client</span>
                    </button>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  render() {
    const { loading, error, lead, products, activities, partner, tracking, toast } = this.state;
    return (
      <div className="nk-content">
        {ReactDOM.createPortal(
          <div style={{ position: "fixed", top: 16, right: 16, zIndex: 9999, width: 340, maxWidth: "calc(100% - 32px)" }}>
            <NotificationToast
              type={toast ? toast.type : "success"}
              message={toast ? toast.msg : ""}
              duration={2000}
              onDismiss={() => this.setState({ toast: null })}
            />
          </div>,
          document.body
        )}
        <div className="container-fluid">
          <div className="nk-content-inner">
            <div className="nk-content-body">
              {loading ? (
                <p>Loading…</p>
              ) : error ? (
                <div className="alert alert-warning">{error}</div>
              ) : !lead ? (
                <div className="alert alert-warning">Lead not found.</div>
              ) : (
                <React.Fragment>
                  {this.renderHeader(lead)}
                  <div className="ld-grid">
                    <LeftPanel
                      lead={lead}
                      partner={partner}
                      products={products}
                      tracking={tracking}
                      onFieldSaved={this.onFieldSaved}
                    />
                    <Center
                      lead={lead}
                      activities={activities}
                      onActivitiesChanged={() => this.refresh()}
                      onToast={this.showToast}
                    />
                    <RightPanel
                      lead={lead}
                      onStageChanged={this.onStageChanged}
                      onConvert={this.onConvert}
                      onArchived={this.onArchived}
                      onToast={this.showToast}
                    />
                  </div>
                </React.Fragment>
              )}
              {lead ? this.renderConvertModal(lead) : null}
            </div>
          </div>
        </div>
      </div>
    );
  }
}
